import { useCallback, useState } from 'react';
import { Tooltip } from 'framework/ui/mui-builder';
import { useFieldHelper } from 'framework/ui/field';
import { InterruptionEmployeeResponseStatus, InterruptionType } from '../types';
import InterruptionActionButton from './layout/InterruptionActionButton';

type InterruptionListCardActionsProps = {
  onUpdateStatus: (id: string, status: InterruptionEmployeeResponseStatus) => Promise<boolean>;
};

const InterruptionListCardActions = ({ onUpdateStatus }: InterruptionListCardActionsProps) => {
  const { getValue: getId } = useFieldHelper<string>({ source: 'id' });
  const { getValue: getType } = useFieldHelper<InterruptionType>({ source: 'type' });
  const [loadingStatus, setLoadingStatus] = useState<InterruptionEmployeeResponseStatus | null>(null);
  const isPending = loadingStatus !== null;
  const id = getId();
  const type = getType();
  const isTaskStatusPing = type === InterruptionType.TaskStatusPing;

  const runAction = useCallback(
    async (status: InterruptionEmployeeResponseStatus) => {
      if (isPending) {
        return;
      }

      setLoadingStatus(status);
      try {
        await onUpdateStatus(id, status);
      } finally {
        setLoadingStatus(null);
      }
    },
    [id, isPending, onUpdateStatus],
  );

  if (!isTaskStatusPing) {
    return null;
  }

  return (
    <>
      <Tooltip title="This interruption will reappear in your next Daily Check-In.">
        <span>
          <InterruptionActionButton
            variant="outlined"
            color="secondary"
            size="small"
            disabled={isPending}
            minWidth={100}
            onClick={() => {
              void runAction(InterruptionEmployeeResponseStatus.WillFinishNextCheckin);
            }}
          >
            {loadingStatus === InterruptionEmployeeResponseStatus.WillFinishNextCheckin ? 'Saving...' : 'Update later'}
          </InterruptionActionButton>
        </span>
      </Tooltip>
      <InterruptionActionButton
        variant="outlined"
        color="success"
        size="small"
        disabled={isPending}
        minWidth={98}
        onClick={() => {
          void runAction(InterruptionEmployeeResponseStatus.WillFinishToday);
        }}
      >
        {loadingStatus === InterruptionEmployeeResponseStatus.WillFinishToday ? 'Saving...' : 'Finish today'}
      </InterruptionActionButton>
    </>
  );
};

export default InterruptionListCardActions;
